import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Text,
  ListItem,
  Alert,
  AlertIcon,
  AlertDescription,
  UnorderedList,
  Grid,
  GridItem,
  Icon,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import { MdCloud } from 'react-icons/md';

import { apiUploadFile } from 'api';
import toUploadErrorMessages from 'helpers/toUploadErrorMessages';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import {
  VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
  VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
  VALIDATION_TYPE_SPECIALTY_AUTO_LOA,
  VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY,
  VALIDATION_TYPE_COMMERCIAL_AUTO,
  CGI_MVR_ENABLED_PROVINCES,
  VALIDATION_TYPE_MC,
  VALIDATION_TYPE_MC_HOME,
} from 'variables/constants';

import FileSummarySection from './components/FileSummarySection';
import EnhancedDropzone from './components/EnhancedDropzone';

const UploadPolicyView = ({
  submissionType,
  submissionId,
  onSubmitUpload,
  isCreatingSubmission = false,
  isLOA = null,
  province = null,
}: {
  submissionType: ValidationType;
  submissionId?: number | null;
  onSubmitUpload?: (uploadedUuid?: string) => void;
  isCreatingSubmission?: boolean;
  isLOA?: boolean | null;
  province?: number | null;
}) => {
  const navigate = useNavigate();
  const isCgiMvrEnabled = useFeatureFlag('enable-cgi-mvr');
  const isDashAutopullEnabled = useFeatureFlag('enable-dash-autopull');
  const showManualMvrUpload =
    !isCgiMvrEnabled ||
    (province !== null && !CGI_MVR_ENABLED_PROVINCES.includes(province));

  const [uploadUUID, setUploadUUID] = useState(null);
  const [uploadError, setUploadError] = useState<string[] | null>(null);
  const [isUploading, setIsUploading] = useState({
    mvr: false,
    autoplus: false,
    quote: false,
    application: false,
  });
  const [isCreatingLOA, setIsCreatingLOA] = useState(false);

  const [uploadedMVR, setUploadedMVRs] = useState([]);
  const [uploadedAutoplus, setUploadedAutoplus] = useState([]);
  const [uploadedQuote, setUploadedQuote] = useState(null);
  const [isManualDriverReport, setIsManualDriverReport] = useState(false);
  const showDriverReportUpload =
    !isDashAutopullEnabled ||
    isManualDriverReport ||
    uploadedAutoplus.length > 0;

  const onFileSelect = async (files: any, title: string) => {
    const type = title.toLowerCase();

    setUploadError(null);
    setIsUploading((prev) => ({ ...prev, [type]: true }));

    let localUUID = uploadUUID;
    for (const file of files) {
      try {
        const uploadResp = await handleFileUpload(file, title, localUUID);
        const fileName = file.name;

        localUUID = uploadResp.uuid;
        setUploadUUID(uploadResp.uuid);

        if (type === 'mvr') {
          setUploadedMVRs((prev) => [...prev, fileName]);
        }

        if (type === 'driver_report') {
          setUploadedAutoplus((prev) => [...prev, fileName]);
        }

        if (type === 'quote') {
          setUploadedQuote(fileName);
        }
      } catch (error) {
        setUploadError(toUploadErrorMessages(error));
      }
    }

    setIsUploading((prev) => ({ ...prev, [type]: false }));
  };

  const handleFileUpload = async (
    file: any,
    title: string,
    uuid: null | string,
  ) => {
    const type = title.toLowerCase();

    const payload = new FormData();
    payload.append(type, file);

    payload.append('type', submissionType);
    if (
      isLOA !== null &&
      (submissionType === VALIDATION_TYPE_MC ||
        submissionType === VALIDATION_TYPE_MC_HOME)
    ) {
      payload.append('is_loa', String(isLOA));
    }

    if (submissionId) {
      payload.append('submission_id', submissionId.toString());
    }

    return await apiUploadFile(uuid ? uuid : null, payload);
  };

  const isButtonDisabled = () => {
    if (
      isUploading.mvr ||
      isUploading.autoplus ||
      isUploading.quote ||
      isUploading.application ||
      isCreatingSubmission ||
      isCreatingLOA
    ) {
      return true;
    }

    // Specialty LOA does not require a quote; personal lines still run validations.
    if (isLOA && submissionType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE) {
      return false;
    }

    return !uploadedQuote;
  };

  const onSubmit = async () => {
    if (onSubmitUpload) {
      return onSubmitUpload(uploadUUID);
    }

    if (submissionId) {
      const isSpecialty =
        submissionType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE ||
        submissionType === VALIDATION_TYPE_VALIDATE_HOME_QUOTE;
      const isCommercial =
        submissionType === VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY ||
        submissionType === VALIDATION_TYPE_COMMERCIAL_AUTO;
      const endpoint = isSpecialty
        ? 'specialty'
        : isCommercial
          ? 'commercial'
          : 'validations';
      navigate(
        `/admin/policies/submission/${submissionId}/${uploadUUID}/${endpoint}`,
      );
    }
  };

  const onLOASubmit = async () => {
    // If submissionId is null, delegate to parent to create submission first
    if (!submissionId) {
      if (onSubmitUpload) {
        return onSubmitUpload(undefined);
      }
      return;
    }

    setIsCreatingLOA(true);
    try {
      // Create LOA policy using /upload API with validate_auto_quote type and is_loa flag
      const formData = new FormData();
      formData.append('type', VALIDATION_TYPE_SPECIALTY_AUTO_LOA);
      formData.append('submission_id', submissionId.toString());

      const response = await apiUploadFile(null, formData);
      const loaUuid = response.uuid;

      if (onSubmitUpload) {
        return onSubmitUpload(loaUuid);
      }

      // Navigate to specialty form for LOA
      navigate(
        `/admin/policies/submission/${submissionId}/${loaUuid}/specialty`,
      );
    } catch (error: any) {
      setUploadError(toUploadErrorMessages(error));
    } finally {
      setIsCreatingLOA(false);
    }
  };

  const resetForm = () => {
    setUploadUUID(null);
    setUploadError(null);
    setIsUploading({
      mvr: false,
      autoplus: false,
      quote: false,
      application: false,
    });

    setUploadedMVRs([]);
    setUploadedAutoplus([]);
    setUploadedQuote(null);
    setIsManualDriverReport(false);
  };

  const getcolumns = () => {
    if (submissionType === 'mc') {
      const columns = showManualMvrUpload ? 2 : 1;
      return showDriverReportUpload ? columns + 1 : columns;
    }
    if (
      submissionType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE ||
      submissionType === VALIDATION_TYPE_VALIDATE_HOME_QUOTE ||
      submissionType === VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY ||
      submissionType === VALIDATION_TYPE_COMMERCIAL_AUTO
    )
      return 1;
  };

  const disableOtherUpload =
    (isUploading.mvr ||
      isUploading.autoplus ||
      isUploading.quote ||
      isUploading.application) &&
    uploadUUID === null;

  // For LOA specialty auto, show a simplified view without quote upload
  if (isLOA && submissionType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE) {
    return (
      <>
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="bold" mb={1}>
              LOA Submission
            </Text>
            LOA submission does not require a quote document. Click "Continue to
            LOA Form" to proceed to the submission form where you will provide
            the required LOA information and documents.
          </AlertDescription>
        </Alert>

        {uploadError && (
          <Alert
            status="error"
            mt="16px"
            size="sm"
            borderRadius="md"
            variant="left-accent"
          >
            <AlertIcon />
            <AlertDescription fontSize="sm">
              <UnorderedList styleType="''">
                {uploadError.map((message) => (
                  <ListItem key={message}>{message}</ListItem>
                ))}
              </UnorderedList>
            </AlertDescription>
          </Alert>
        )}

        <Card mt="16px" p="16px">
          <Flex justifyContent="flex-end">
            <Button
              variant="brand"
              isDisabled={isCreatingSubmission || isCreatingLOA}
              isLoading={isCreatingLOA}
              loadingText="Creating LOA..."
              onClick={onLOASubmit}
              size="md"
              colorScheme="green"
            >
              Continue to LOA Form
            </Button>
          </Flex>
        </Card>
      </>
    );
  }

  return (
    <>
      <Grid templateColumns={`repeat(${getcolumns()}, 1fr)`} gap="12px">
        {[
          'mc',
          'mc_home',
          VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
          VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
        ].includes(submissionType) && (
          <GridItem>
            <EnhancedDropzone
              title="Quote"
              handleFileUpload={(files: any) => {
                // Only upload one quote file at a time
                if (files.length > 0) {
                  onFileSelect([files[0]], 'quote');
                }
              }}
              isLoading={disableOtherUpload || isUploading.quote}
              isComplete={!!uploadedQuote}
              required={true}
            />
          </GridItem>
        )}

        {submissionType === 'mc' && (
          <>
            {showManualMvrUpload && (
              <GridItem>
                <EnhancedDropzone
                  title="MVR"
                  handleFileUpload={(files: any) => onFileSelect(files, 'mvr')}
                  isLoading={disableOtherUpload || isUploading.mvr}
                  isComplete={uploadedMVR.length > 0}
                  fileCount={uploadedMVR.length}
                />
              </GridItem>
            )}
            {showDriverReportUpload && (
              <GridItem>
                <EnhancedDropzone
                  title="AutoPlus/DASH"
                  handleFileUpload={(files: any) =>
                    onFileSelect(files, 'driver_report')
                  }
                  isLoading={disableOtherUpload || isUploading.autoplus}
                  isComplete={uploadedAutoplus.length > 0}
                  fileCount={uploadedAutoplus.length}
                />
                {!isDashAutopullEnabled && (
                  <Text fontSize="xs" color="gray.500" mt={1} ml={2}>
                    If no files are provided, the system will try to
                    automatically pull the DASH reports for all the drivers on
                    the Quote if the Carrier supports it
                  </Text>
                )}
              </GridItem>
            )}
          </>
        )}
      </Grid>

      {submissionType === 'mc' && isDashAutopullEnabled && (
        <Box mt="12px">
          <Checkbox
            isChecked={showDriverReportUpload}
            isDisabled={uploadedAutoplus.length > 0}
            onChange={(event) => setIsManualDriverReport(event.target.checked)}
            colorScheme="blue"
          >
            <Text fontSize="sm">Manually upload AutoPlus/DASH</Text>
          </Checkbox>
          {!showDriverReportUpload && (
            <Text fontSize="xs" color="gray.500" mt={1}>
              The system will try to automatically pull the DASH reports for all
              the drivers on the Quote if the Carrier supports it, using the
              years configured for that Carrier and province. Check the box
              above to upload AutoPlus/DASH manually instead.
            </Text>
          )}
        </Box>
      )}

      {uploadError && (
        <Alert
          status="error"
          mt="16px"
          size="sm"
          borderRadius="md"
          variant="left-accent"
        >
          <AlertIcon />
          <AlertDescription fontSize="sm">
            <UnorderedList styleType="''">
              {uploadError.map((message) => (
                <ListItem key={message}>{message}</ListItem>
              ))}
            </UnorderedList>
          </AlertDescription>
        </Alert>
      )}

      <Card mt="16px" p="16px">
        <Text fontWeight="600" fontSize="md" mb="12px">
          Uploaded Files Summary
        </Text>
        <FileSummarySection
          uploadedQuote={uploadedQuote}
          uploadedMVR={uploadedMVR}
          uploadedAutoplus={uploadedAutoplus}
          hasQuote={[
            'mc',
            'mc_home',
            VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
            VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
          ].includes(submissionType)}
          hasMVR={submissionType === 'mc' && showManualMvrUpload}
          hasDriverReport={submissionType === 'mc' && showDriverReportUpload}
        />
      </Card>

      <Card mt="16px" p="16px">
        <Flex justifyContent="space-between" gap="12px">
          <Button
            size="md"
            onClick={resetForm}
            variant="outline"
            leftIcon={<Icon as={MdCloud} />}
          >
            Reset Form
          </Button>

          <Button
            variant="brand"
            isDisabled={isButtonDisabled()}
            onClick={onSubmit}
            size="md"
            flexGrow={1}
            colorScheme="green"
          >
            Initiate Policy Check
          </Button>
        </Flex>
      </Card>
    </>
  );
};

export default UploadPolicyView;
